import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Sparkles, Upload, Loader2, CheckCircle2, ArrowRight,
  Image as ImageIcon, Video, BookOpen, Send, Calendar, RefreshCcw, Check, Globe2, Play, Wand2,
  Plus, RotateCcw, AlertTriangle, Mail, Wallet, Download,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// TeckiA CM Studio - creation manuelle. Un seul ecran de prompt structure en
// 4 champs (Environnement / Ambiance / Mouvement / Action) + un champ
// Dialogue, utilisable a partir de zero OU pre-rempli en choisissant un
// template dans la bibliotheque (le template n'est qu'une couche de
// visualisation - avatar/produit/dialogue restent librement modifiables).
// ============================================================

const authJson = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});
const authMultipart = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

const MAX_SELECTED_PHOTOS = 5;

// Images de reference d'un MODELE de la bibliotheque : au plus 4. Les emplacements d'origine du modele (produit, avatar...) restent identiques (le texte du modele y fait reference par @imageN) ;
// on y ajoute des emplacements facultatifs : la personne qui presente, le decor / arriere-plan, d'autres references.
const MAX_TEMPLATE_REFS = 4;
const roleOfLabel = (label) => (/avatar|personne|pr[eé]sentat|mannequin/i.test(label || '') ? 'person' : /d[eé]cor|fond|arri[eè]re|lieu|background/i.test(label || '') ? 'background' : 'product');
const templateSlots = (tpl) => {
  if (!tpl) return [];
  const labels = (tpl.slot_labels && tpl.slot_labels.length ? tpl.slot_labels : new Array(tpl.image_slots || 0).fill('Produit')).slice(0, MAX_TEMPLATE_REFS);
  const slots = labels.map(label => ({ label, role: roleOfLabel(label), extra: false }));
  if (!slots.some(x => x.role === 'person')) slots.push({ label: 'Personne qui présente', role: 'person', extra: true });
  if (!slots.some(x => x.role === 'background')) slots.push({ label: 'Décor / arrière-plan', role: 'background', extra: true });
  while (slots.length < MAX_TEMPLATE_REFS) slots.push({ label: 'Autre référence', role: 'other', extra: true });
  return slots.slice(0, MAX_TEMPLATE_REFS);
};
// Tarifs CM Studio (Ariary) - doivent rester identiques a ceux de cm_routes.py
// (source de verite backend) ; affiches ici uniquement a titre indicatif avant generation.
const PRICE_VIDEO_MG = 7000;
const PRICE_VIDEO_OTHER = 5000;
const PRICE_IMAGE = 600;
const POLL_MS = 4000;

const REACTION_CUES = [
  'Calme', 'Excité(e)', 'Chuchote', 'Rit', 'Soupire', 'Crie de joie',
  'Montre le produit à la caméra', 'Touche le produit du bout des doigts',
];

const VIDEO_STYLES = [
  { id: 'ugc', label: 'UGC (recommandé)', desc: 'Une personne présente le produit, style authentique — le plus fiable' },
  { id: 'ai_ad', label: 'Publicité IA', desc: 'Style pub professionnelle avec voix off' },
  { id: 'from_photos', label: 'Animer vos photos', desc: 'Vos photos prennent vie avec voix off' },
];

const STRUCTURED_FIELDS = [
  { key: 'environment', label: 'Environnement', placeholder: 'Où se déroule la scène ? Ex. « Un salon lumineux avec de grandes fenêtres... »' },
  { key: 'ambiance', label: 'Ambiance', placeholder: 'Lumière, énergie, style de prise de vue... Ex. « Chaleureux, style selfie iPhone, sans filtre... »' },
  { key: 'outfit', label: 'Outfit / tenue', placeholder: "Ce que porte la personne (si pertinent). Ex. « Porte la tenue montrée dans @image2... »" },
  { key: 'movement', label: 'Mouvement', placeholder: 'Mouvements de caméra ou du produit. Ex. « La caméra suit lentement le produit qui tourne... »' },
  { key: 'action', label: 'Action', placeholder: 'Ce que fait la personne avec le produit. Ex. « Elle tient @image1 et montre @image2 à la caméra... »' },
];

function TeckiaOrb({ size = 'lg' }) {
  const dim = size === 'lg' ? 'w-24 h-24' : 'w-9 h-9';
  return (
    <div className={`relative ${dim} flex-shrink-0`}>
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 opacity-25 animate-ping" />
      <div className="absolute inset-1 rounded-full bg-gradient-to-br from-fuchsia-500 to-blue-500 opacity-30 animate-pulse" />
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 via-fuchsia-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-900/50 animate-[spin_7s_linear_infinite]">
        <div className="w-[82%] h-[82%] rounded-full bg-slate-950 flex items-center justify-center">
          <Sparkles className={size === 'lg' ? 'w-9 h-9 text-purple-300 animate-pulse' : 'w-4 h-4 text-purple-300 animate-pulse'} />
        </div>
      </div>
    </div>
  );
}

export default function TeckiaCMStudioModal({ clientId, cfg, creditBalance = 0, onClose, onPublished, pub = false, onTopup }) {   // pub : TeckiA Pub (meme studio, sans Facebook : on telecharge au lieu de publier)
  const [mounted, setMounted] = useState(false);
  const [step, setStep] = useState('prompt'); // prompt | generating | result
  const [error, setError] = useState('');
  const [insufficientCredit, setInsufficientCredit] = useState(null); // {balance, price} | null

  const [catalog, setCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryCategory, setLibraryCategory] = useState('Tous');
  const [library, setLibrary] = useState([]);
  const [mediaKind, setMediaKind] = useState('image');
  const [videoStyle, setVideoStyle] = useState('ugc');
  const [videoLanguage, setVideoLanguage] = useState(cfg?.video_language || 'fr');
  const [hasDialogue, setHasDialogue] = useState(true);

  // Template choisi (optionnel) - une couche de visualisation qui pre-remplit
  // les champs ci-dessous, mais tout reste modifiable/remplaçable.
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [slotPhotos, setSlotPhotos] = useState([]);
  const [pickingSlot, setPickingSlot] = useState(null);

  const [fieldEnvironment, setFieldEnvironment] = useState('');
  const [fieldAmbiance, setFieldAmbiance] = useState('');
  const [fieldOutfit, setFieldOutfit] = useState('');
  const [fieldMovement, setFieldMovement] = useState('');
  const [fieldAction, setFieldAction] = useState('');
  const [dialogueText, setDialogueText] = useState('');
  const [translating, setTranslating] = useState(false);

  const [showAdapt, setShowAdapt] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [adapting, setAdapting] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [resultPost, setResultPost] = useState(null);
  const [caption, setCaption] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [acting, setActing] = useState(false);
  const [done, setDone] = useState(null); // 'published' | 'scheduled'

  const fileInputRef = useRef(null);
  const slotFileInputRef = useRef(null);
  const pollRef = useRef(null);
  const postIdRef = useRef(null);
  const dialogueRef = useRef(null);
  const fieldRefs = useRef({});
  const lastFocusedFieldRef = useRef('action');

  const fieldSetters = {
    environment: setFieldEnvironment, ambiance: setFieldAmbiance, outfit: setFieldOutfit,
    movement: setFieldMovement, action: setFieldAction,
  };
  const fieldValues = {
    environment: fieldEnvironment, ambiance: fieldAmbiance, outfit: fieldOutfit,
    movement: fieldMovement, action: fieldAction,
  };

  const insertImageTag = (n) => {
    const key = lastFocusedFieldRef.current || 'action';
    const tag = `@image${n} `;
    const el = fieldRefs.current[key];
    const current = fieldValues[key] || '';
    if (el && document.activeElement === el && el.selectionStart != null) {
      const pos = el.selectionStart;
      const next = current.slice(0, pos) + tag + current.slice(pos);
      fieldSetters[key](next);
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = pos + tag.length; }, 0);
    } else {
      fieldSetters[key]((current ? `${current} ` : '') + tag);
    }
  };

  const insertReactionCue = (label) => {
    const cue = `[${label}] `;
    const el = dialogueRef.current;
    if (el && document.activeElement === el && el.selectionStart != null) {
      const pos = el.selectionStart;
      const next = dialogueText.slice(0, pos) + cue + dialogueText.slice(pos);
      setDialogueText(next);
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = pos + cue.length; }, 0);
    } else {
      setDialogueText(prev => (prev ? `${prev} ` : '') + cue);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 20);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadPhotos();
    loadLibrary();
    return () => {
      clearTimeout(t);
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPhotos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/photos`, { headers: authJson() });
      const data = await res.json();
      if (data.success) setCatalog(data.photos.filter(p => !p.is_avatar));
    } catch (e) { console.error(e); }
  }, [clientId]);

  const loadLibrary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/prompt-library`, { headers: authJson() });
      const data = await res.json();
      if (data.success) setLibrary(data.prompts);
    } catch (e) { console.error(e); }
  }, []);

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setMounted(false);
    setTimeout(onClose, 200);
  };

  const togglePhoto = (id) => {
    setSelectedIds(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : (prev.length >= MAX_SELECTED_PHOTOS ? prev : [...prev, id]));
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList).slice(0, MAX_SELECTED_PHOTOS);
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('photos', f));
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/photos`, {
        method: 'POST', headers: authMultipart(), body: fd
      });
      const data = await res.json();
      if (data.success) {
        setCatalog(prev => [...data.photos, ...prev]);
        setSelectedIds(prev => [...prev, ...data.photos.map(p => p.id)].slice(0, MAX_SELECTED_PHOTOS));
      } else {
        setError(data.message || "Erreur d'upload");
      }
    } catch {
      setError('Erreur serveur');
    } finally { setUploading(false); }
  };

  const deletePhoto = async (photoId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Supprimer definitivement cette photo ?')) return;
    try {
      await fetch(`${API_URL}/api/cm/config/${clientId}/photos/${photoId}`, { method: 'DELETE', headers: authJson() });
    } catch (err) { console.error(err); }
    setCatalog(prev => prev.filter(p => p.id !== photoId));
    setSelectedIds(prev => prev.filter(id => id !== photoId));
    setSlotPhotos(prev => prev.map(p => (p && p.id === photoId) ? null : p));
  };

  const openTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setSlotPhotos(new Array(templateSlots(tpl).length).fill(null));
    setFieldEnvironment(tpl.environment || '');
    setFieldAmbiance(tpl.ambiance || '');
    setFieldOutfit(tpl.outfit || '');
    setFieldMovement(tpl.movement || '');
    setFieldAction(tpl.action || (tpl.visual_prompt && !tpl.environment ? tpl.visual_prompt : ''));
    setDialogueText(tpl.dialogue_en || '');
    setMediaKind(tpl.media_kind);
    setVideoStyle(tpl.video_style || 'ugc');
    setHasDialogue(!!tpl.has_dialogue);
    setShowAdapt(false);
    setExplanation('');
    setError('');
    setShowLibrary(false);
  };

  const clearTemplate = () => {
    setSelectedTemplate(null);
    setSlotPhotos([]);
    setFieldEnvironment(''); setFieldAmbiance(''); setFieldOutfit(''); setFieldMovement(''); setFieldAction('');
    setDialogueText('');
  };

  const applyLanguage = async (lang) => {
    setVideoLanguage(lang);
    const sourceDialogue = selectedTemplate?.dialogue_en;
    if (!hasDialogue || !sourceDialogue) return;
    if (lang === 'en') { setDialogueText(sourceDialogue); return; }
    setTranslating(true);
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/studio/translate-dialogue`, {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ text: sourceDialogue, video_language: lang }),
      });
      const data = await res.json();
      if (data.success) setDialogueText(data.text);
    } catch (e) { console.error(e); } finally { setTranslating(false); }
  };

  const assignSlot = (index, photo) => {
    setSlotPhotos(prev => { const next = [...prev]; next[index] = photo; return next; });
    setPickingSlot(null);
  };

  const uploadForSlot = async (index, fileList) => {
    const file = fileList[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('photos', file);
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/photos`, { method: 'POST', headers: authMultipart(), body: fd });
      const data = await res.json();
      if (data.success && data.photos[0]) {
        setCatalog(prev => [...data.photos, ...prev]);
        assignSlot(index, data.photos[0]);
      } else {
        setError(data.message || "Erreur d'upload");
      }
    } catch {
      setError('Erreur serveur');
    } finally { setUploading(false); }
  };

  const pollPost = (postId) => {
    postIdRef.current = postId;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/cm/posts/${clientId}`, { headers: authJson() });
        const data = await res.json();
        const p = (data.posts || []).find(x => x.id === postId);
        if (p && p.status !== 'generating') {
          clearInterval(pollRef.current);
          setGenerating(false);
          if (p.status === 'failed') {
            setError(p.error_message || 'La generation a echoue.');
            setStep('prompt');
          } else {
            setResultPost(p);
            setStep('result');
          }
          return;
        }
      } catch (e) { console.error(e); }
      // Pas de delai maximum : on attend le vrai resultat (termine ou echoue). Une video a deja pris 6 min 48 s alors qu'elle etait bien livree.
    }, POLL_MS);
  };

  const joinFields = () => [fieldEnvironment, fieldAmbiance, fieldOutfit, fieldMovement, fieldAction]
    .map(s => s.trim()).filter(Boolean).join(' ');

  const currentPhotoIds = () => (
    selectedTemplate
      ? slotPhotos.filter(Boolean).map(p => p.id)
      : selectedIds
  );

  const estimatedPrice = mediaKind === 'video'
    ? (videoLanguage === 'mg' ? PRICE_VIDEO_MG : PRICE_VIDEO_OTHER)
    : PRICE_IMAGE;

  // Les references ajoutees au modele (personne, decor, autre) sont decrites dans le prompt : le moteur sait a quoi sert chaque image. @imageN = rang reel de la photo envoyee.
  const refNote = () => {
    if (!selectedTemplate) return '';
    const defs = templateSlots(selectedTemplate);
    const sent = slotPhotos.map((ph, i) => (ph ? defs[i] : null)).filter(Boolean);
    const notes = [];
    const written = joinFields();
    sent.forEach((def, k) => {
      if (!def.extra) return;
      const n = k + 1;
      if (new RegExp(`@image${n}(?!\\d)`).test(written)) return;   // le texte decrit deja cette image : une 2e consigne la contredirait
      if (def.role === 'person') notes.push(`Any person appearing on camera is exactly the person shown in @image${n} (same face, hair, skin tone and body), replacing any other person described above; never invent a different person.`);
      else if (def.role === 'background') notes.push(`The scene takes place in the setting shown in @image${n} (same place, decor and lighting), used as the background.`);
      else notes.push(`@image${n} is an additional visual reference: keep its subject faithful and show it in the scene.`);
    });
    return notes.join(' ');
  };

  const generate = async (enhance) => {
    const visual = [joinFields(), refNote()].filter(Boolean).join(' ');
    const finalPrompt = (hasDialogue && dialogueText.trim())
      ? `${visual} The person says exactly: "${dialogueText.trim()}"`
      : visual;
    if (!joinFields().trim() && !(hasDialogue && dialogueText.trim())) {
      setError('Remplissez au moins un champ (Environnement, Ambiance, Mouvement ou Action).');
      return;
    }
    setError('');
    setInsufficientCredit(null);
    setGenerating(true);
    setStep('generating');
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/studio/generate`, {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({
          prompt: finalPrompt, enhance, photo_ids: currentPhotoIds(),
          media_kind: mediaKind, video_style: videoStyle, video_language: videoLanguage,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        if (data.error_code === 'insufficient_credit') {
          setInsufficientCredit({ balance: data.balance ?? 0, price: data.price ?? estimatedPrice });
        } else {
          setError(data.message || 'Erreur de generation');
        }
        setGenerating(false);
        setStep('prompt');
        return;
      }
      pollPost(data.post_id);
    } catch {
      setError('Erreur serveur');
      setGenerating(false);
      setStep('prompt');
    }
  };

  const adaptTemplate = async () => {
    setAdapting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${clientId}/studio/adapt-prompt`, {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          photo_ids: currentPhotoIds(),
          user_explanation: explanation, video_language: videoLanguage,
          photo_roles: Object.fromEntries(slotPhotos.map((ph, i) => (ph ? [ph.id, templateSlots(selectedTemplate)[i]?.role || 'product'] : null)).filter(Boolean)),
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Erreur adaptation');
        return;
      }
      // Le resultat adapte remplace l'action (la partie la plus liee au produit/personne),
      // l'environnement/ambiance/mouvement du template restent inchanges.
      setFieldAction(data.visual_prompt || data.prompt || '');
      if (data.dialogue) setDialogueText(data.dialogue);
      setShowAdapt(false);
    } catch {
      setError('Erreur serveur');
    } finally { setAdapting(false); }
  };

  const regenerate = () => {
    setResultPost(null);
    setError('');
    setStep('prompt');
  };

  const publishNow = async () => {
    if (!resultPost) return;
    setActing(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/cm/posts/${clientId}/${resultPost.id}/approve`, {
        method: 'POST', headers: authJson(), body: JSON.stringify({ caption }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Erreur de publication');
        setActing(false);
        return;
      }
      setDone(pub ? 'saved' : 'published');
      if (onPublished) onPublished();
    } catch {
      setError('Erreur serveur');
    } finally { setActing(false); }
  };

  const confirmSchedule = async () => {
    if (!resultPost || !scheduledAt) return;
    setActing(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/cm/posts/${clientId}/${resultPost.id}/schedule`, {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ caption, scheduled_at: new Date(scheduledAt).toISOString() }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Erreur de planification');
        setActing(false);
        return;
      }
      setDone('scheduled');
      if (onPublished) onPublished();
    } catch {
      setError('Erreur serveur');
    } finally { setActing(false); }
  };

  const categories = ['Tous', ...Array.from(new Set(library.map(t => t.category).filter(Boolean)))];
  const filteredLibrary = libraryCategory === 'Tous' ? library : library.filter(t => t.category === libraryCategory);

  const renderTemplatePreview = (tpl, className) => (
    tpl.preview_url && tpl.preview_kind === 'video' ? (
      <video src={tpl.preview_url} muted loop playsInline autoPlay className={className} />
    ) : tpl.preview_url ? (
      <img src={tpl.preview_url} alt={tpl.title} className={className} />
    ) : (
      <div className={`${className} flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900`}>
        {tpl.media_kind === 'video' ? <Video className="w-6 h-6 text-gray-600" /> : <ImageIcon className="w-6 h-6 text-gray-600" />}
      </div>
    )
  );

  const slotsDef = templateSlots(selectedTemplate);
  const slotLabels = slotsDef.map(x => x.label);

  return createPortal((
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
        mounted ? 'bg-black/70 backdrop-blur-sm' : 'bg-black/0 backdrop-blur-0 pointer-events-none'
      }`}
    >
      <div
        className={`w-full max-w-2xl rounded-3xl border border-purple-500/30 bg-gradient-to-b from-slate-900 via-slate-900 to-purple-950/40 shadow-2xl shadow-purple-900/50 overflow-hidden transition-all duration-300 max-h-[90vh] flex flex-col ${
          mounted ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-purple-600/20 to-blue-600/10 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <TeckiaOrb size="sm" />
            <div>
              <div className="text-white font-semibold text-sm">{pub ? 'TeckiA Pub' : 'TeckiA CM Studio'}</div>
              <div className="text-gray-400 text-[11px]">
                {step === 'prompt' && (selectedTemplate ? selectedTemplate.title : (pub ? 'Votre idée de pub' : 'Votre idée de publication'))}
                {step === 'generating' && 'Génération en cours...'}
                {step === 'result' && (pub ? 'Aperçu & téléchargement' : 'Aperçu & publication')}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Solde & coût - toujours visible, même en scrollant */}
        <div className={`px-6 py-2 flex items-center justify-between gap-3 text-[11px] flex-shrink-0 border-b ${
          insufficientCredit ? 'bg-red-500/10 border-red-500/20' :
          creditBalance < estimatedPrice ? 'bg-amber-500/10 border-amber-500/20' : 'bg-black/25 border-white/5'
        }`}>
          <span className="flex items-center gap-1.5 text-gray-300">
            <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
            Solde :{' '}
            <span className={`font-semibold ${insufficientCredit || creditBalance < estimatedPrice ? 'text-amber-300' : 'text-emerald-300'}`}>
              {creditBalance.toLocaleString('fr-FR')} Ar
            </span>
          </span>
          {step === 'prompt' && (
            <span className="text-gray-400 flex-shrink-0">
              Coût de cette génération : <span className="text-gray-200 font-medium">{estimatedPrice.toLocaleString('fr-FR')} Ar</span>
            </span>
          )}
        </div>
        {insufficientCredit && (
          <div className="px-6 py-2 flex items-start gap-2 text-[11px] bg-red-500/15 text-red-200 border-b border-red-500/20 flex-shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Solde insuffisant : il vous manque{' '}
              <span className="font-semibold">{Math.max(0, insufficientCredit.price - insufficientCredit.balance).toLocaleString('fr-FR')} Ar</span>.
              {pub ? (
                <button type="button" onClick={() => { if (onTopup) onTopup(); }} className="underline font-semibold">Recharger mon solde</button>
              ) : (<>
              Réapprovisionnez votre compte (Mvola, Orange Money ou carte) —{' '}
              <a href="mailto:contact@tdidigital.io" className="underline inline-flex items-center gap-1">
                <Mail className="w-3 h-3" /> contact@tdidigital.io
              </a></>)}
            </span>
          </div>
        )}

        {/* Progress dots */}
        <div className="flex gap-1.5 px-6 pt-4 flex-shrink-0">
          {['prompt', 'result'].map((s) => {
            const order = ['prompt', 'generating', 'result'];
            const active = order.indexOf(step) >= order.indexOf(s);
            return <div key={s} className={`h-1 flex-1 rounded-full transition-all duration-300 ${active ? 'bg-purple-400' : 'bg-white/10'}`} />;
          })}
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-[320px]">
          {done ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-400" />
              </div>
              <div className="text-white font-medium">
                {done === 'saved' ? 'Enregistrée dans vos pubs !' : done === 'published' ? 'Publié sur votre Page !' : 'Publication planifiée !'}
              </div>
              <div className="text-gray-400 text-xs text-center">
                {done === 'saved' ? 'Retrouvez-la dans « Mes pubs » pour la télécharger.' : done === 'published' ? 'Votre contenu est en ligne.' : `Elle partira automatiquement le ${new Date(scheduledAt).toLocaleString('fr-FR')}.`}
              </div>
              <button onClick={handleClose} className="mt-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium">
                Fermer
              </button>
            </div>
          ) : step === 'prompt' ? (
            <div className="space-y-4">
              {selectedTemplate && (
                <div>
                  <div className="rounded-xl overflow-hidden border border-white/10 bg-black flex items-center justify-center">
                    {renderTemplatePreview(selectedTemplate, "w-full max-h-[45vh] object-contain")}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div>
                      <div className="text-white text-sm font-semibold">{selectedTemplate.title}</div>
                      {selectedTemplate.desc && <div className="text-gray-400 text-xs mt-0.5">{selectedTemplate.desc}</div>}
                    </div>
                    <button onClick={clearTemplate} className="flex-shrink-0 flex items-center gap-1 text-gray-500 hover:text-gray-300 text-[10px]">
                      <RotateCcw className="w-3 h-3" /> Repartir de zéro
                    </button>
                  </div>
                </div>
              )}

              {(selectedTemplate ? slotLabels.length > 0 : true) && (
                <div>
                  <div className="text-gray-400 text-xs mb-1.5">
                    {selectedTemplate ? 'Remplacez par vos photos : produit, personne qui présente, décor… (jusqu’à 4 références)' : `Photos de référence (optionnel, jusqu'à ${MAX_SELECTED_PHOTOS})`}
                  </div>
                  {selectedTemplate ? (
                    <div className="flex gap-2">
                      {slotLabels.map((label, i) => {
                        const photo = slotPhotos[i];
                        return (
                          <div key={i} className="flex-1">
                            <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-white/10 bg-black/20">
                              {photo ? (
                                <>
                                  <img src={photo.url} alt={label} className="w-full h-full object-cover" />
                                  <button
                                    onClick={() => assignSlot(i, null)}
                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setPickingSlot(pickingSlot === i ? null : i)}
                                  className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-purple-300"
                                >
                                  <Plus className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                            <div className="text-gray-500 text-[10px] text-center mt-1 truncate">{label}{slotsDef[i]?.extra ? <span className="text-gray-600"> · option</span> : null}</div>
                            {pickingSlot === i && (
                              <div className="mt-1.5 bg-black/40 border border-purple-500/20 rounded-lg p-1.5 space-y-1.5">
                                <div className="grid grid-cols-4 gap-1 max-h-20 overflow-y-auto">
                                  {catalog.map(p => (
                                    <div key={p.id} className="relative aspect-square">
                                      <button onClick={() => assignSlot(i, p)} className="w-full h-full rounded overflow-hidden border border-white/10">
                                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                                      </button>
                                      <button
                                        onClick={(e) => deletePhoto(p.id, e)}
                                        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500/90 text-white flex items-center justify-center"
                                      >
                                        <X className="w-2 h-2" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <button
                                  onClick={() => slotFileInputRef.current?.click()}
                                  disabled={uploading}
                                  className="w-full flex items-center justify-center gap-1 py-1 rounded bg-black/30 text-[10px] text-gray-300"
                                >
                                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Envoyer
                                </button>
                                <input ref={slotFileInputRef} type="file" accept="image/*" hidden
                                  onChange={e => e.target.files && uploadForSlot(i, e.target.files)} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-6 gap-1.5">
                      {catalog.map(p => {
                        const selected = selectedIds.includes(p.id);
                        return (
                          <div key={p.id} className="relative aspect-square">
                            <button
                              onClick={() => togglePhoto(p.id)}
                              className={`w-full h-full rounded-lg overflow-hidden border-2 transition-all ${
                                selected ? 'border-purple-400' : 'border-white/10 hover:border-white/25'
                              }`}
                            >
                              <img src={p.url} alt={p.name || ''} className="w-full h-full object-cover" />
                              {selected && (
                                <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </button>
                            <button
                              onClick={(e) => deletePhoto(p.id, e)}
                              title="Supprimer definitivement"
                              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/90 hover:bg-red-500 text-white flex items-center justify-center z-10"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="rounded-lg border-2 border-dashed border-white/15 hover:border-purple-400/50 text-gray-500 hover:text-purple-300 transition-colors aspect-square flex items-center justify-center"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
                        onChange={e => e.target.files && uploadFiles(e.target.files)} />
                    </div>
                  )}
                </div>
              )}

              {!selectedTemplate && (
                <div>
                  <div className="text-white text-sm font-medium mb-1.5">Que voulez-vous publier ?</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMediaKind('image')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        mediaKind === 'image' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-black/20 border-white/10 text-gray-300'
                      }`}
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Image
                    </button>
                    <button
                      onClick={() => setMediaKind('video')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        mediaKind === 'video' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-black/20 border-white/10 text-gray-300'
                      }`}
                    >
                      <Video className="w-3.5 h-3.5" /> Vidéo
                    </button>
                  </div>
                </div>
              )}

              {!selectedTemplate && mediaKind === 'video' && (
                <div className="space-y-2">
                  <div className="text-gray-400 text-xs">Style de vidéo</div>
                  {VIDEO_STYLES.map(vs => (
                    <button
                      key={vs.id}
                      onClick={() => setVideoStyle(vs.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                        videoStyle === vs.id ? 'bg-purple-600/20 border-purple-400' : 'bg-black/20 border-white/10'
                      }`}
                    >
                      <div className="text-white text-xs font-medium">{vs.label}</div>
                      <div className="text-gray-400 text-[11px]">{vs.desc}</div>
                    </button>
                  ))}
                </div>
              )}

              {mediaKind === 'video' && (
                <div className="flex items-center gap-2">
                  <Globe2 className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" />
                  <select
                    value={videoLanguage}
                    onChange={e => applyLanguage(e.target.value)}
                    className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
                  >
                    {videoStyle === 'ugc' && <option value="mg">🇲🇬 Malagasy</option>}
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 Anglais</option>
                  </select>
                  <span className="text-gray-500 text-[10px]">{videoLanguage === 'mg' ? '(10s max)' : '(15s max)'}</span>
                  {translating && <Loader2 className="w-3 h-3 text-purple-300 animate-spin" />}
                  {!selectedTemplate && (
                    <label className="flex items-center gap-1.5 ml-auto text-gray-400 text-[11px]">
                      <input type="checkbox" checked={hasDialogue} onChange={e => setHasDialogue(e.target.checked)} className="accent-purple-500" />
                      Dialogue parlé
                    </label>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-white text-sm font-medium">Votre prompt</div>
                <button
                  onClick={() => setShowLibrary(v => !v)}
                  className="flex items-center gap-1 text-purple-300 hover:text-purple-200 text-[11px] font-medium"
                >
                  <BookOpen className="w-3.5 h-3.5" /> Bibliothèque de prompts
                </button>
              </div>

              {showLibrary && (
                <div className="bg-black/30 rounded-xl p-3 border border-purple-500/20">
                  <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setLibraryCategory(cat)}
                        className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                          libraryCategory === cat ? 'bg-purple-600 text-white' : 'bg-black/30 text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                    {library.length === 0 ? (
                      <div className="col-span-full text-gray-500 text-xs text-center py-6">Chargement...</div>
                    ) : filteredLibrary.map(tpl => (
                      <button
                        key={tpl.id}
                        onClick={() => openTemplate(tpl)}
                        className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-purple-400/60 transition-all text-left bg-black/40"
                      >
                        <div className="relative w-full aspect-[9/13] overflow-hidden">
                          {renderTemplatePreview(tpl, "w-full h-full object-cover group-hover:scale-105 transition-transform duration-300")}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                          {tpl.media_kind === 'video' && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
                              <Play className="w-2.5 h-2.5 text-white fill-white" />
                            </div>
                          )}
                          <div className="absolute bottom-0 inset-x-0 p-2">
                            <div className="text-white text-[11px] font-semibold leading-tight">{tpl.title}</div>
                            {tpl.desc && <div className="text-gray-300 text-[9px] leading-tight mt-0.5 line-clamp-2">{tpl.desc}</div>}
                          </div>
                          <div className="absolute inset-0 bg-purple-600/0 group-hover:bg-purple-600/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <span className="px-2.5 py-1 rounded-full bg-purple-500 text-white text-[10px] font-semibold flex items-center gap-1">
                              <Wand2 className="w-3 h-3" /> Choisir
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const legendImages = selectedTemplate
                  ? slotPhotos.map((p, i) => p ? { url: p.url, label: slotLabels[i] || `Photo ${i + 1}` } : null).filter(Boolean).map((x, k) => ({ ...x, n: k + 1 }))
                  : selectedIds.map((id, i) => {
                      const p = catalog.find(c => c.id === id);
                      return p ? { n: i + 1, url: p.url, label: p.name || `Photo ${i + 1}` } : null;
                    }).filter(Boolean);
                if (legendImages.length === 0) return null;
                return (
                  <div className="bg-black/25 rounded-xl p-2.5 border border-white/10">
                    <div className="text-gray-400 text-[10px] mb-1.5">Cliquez pour insérer la référence dans le champ actif</div>
                    <div className="flex flex-wrap gap-1.5">
                      {legendImages.map(img => (
                        <button
                          key={img.n}
                          onClick={() => insertImageTag(img.n)}
                          className="flex items-center gap-1.5 pr-2 rounded-full bg-black/40 border border-white/10 hover:border-purple-400/50 transition-colors"
                        >
                          <img src={img.url} alt={img.label} className="w-6 h-6 rounded-full object-cover" />
                          <span className="text-purple-300 text-[10px] font-mono">@image{img.n}</span>
                          <span className="text-gray-500 text-[10px]">{img.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-3">
                {STRUCTURED_FIELDS.map(f => (
                  <div key={f.key}>
                    <label className="text-white text-xs font-medium">{f.label}</label>
                    <textarea
                      ref={el => { fieldRefs.current[f.key] = el; }}
                      value={fieldValues[f.key]}
                      onFocus={() => { lastFocusedFieldRef.current = f.key; }}
                      onChange={e => fieldSetters[f.key](e.target.value)}
                      rows={2}
                      placeholder={f.placeholder}
                      className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
                    />
                  </div>
                ))}
              </div>

              {hasDialogue && (
                <div>
                  <label className="text-white text-xs font-medium">
                    Dialogue parlé {videoLanguage === 'mg' ? '(en malagasy, 10s max)' : videoLanguage === 'fr' ? '(en français, 15s max)' : '(en anglais, 15s max)'}
                  </label>
                  <textarea
                    ref={dialogueRef}
                    value={dialogueText}
                    onChange={e => setDialogueText(e.target.value)}
                    rows={3}
                    disabled={translating}
                    placeholder="Ce que dit la personne, mot pour mot..."
                    className="w-full mt-1 bg-black/30 border border-purple-500/30 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 disabled:opacity-50"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {REACTION_CUES.map(cue => (
                      <button
                        key={cue}
                        onClick={() => insertReactionCue(cue)}
                        className="px-2 py-1 rounded-full bg-black/30 border border-white/10 hover:border-purple-400/50 text-gray-300 hover:text-purple-200 text-[10px] transition-colors"
                      >
                        + {cue}
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-500 text-[10px] mt-1">Cliquez pour insérer une réaction entre les répliques, à placer où vous voulez dans le texte.</p>
                </div>
              )}

              {selectedTemplate && (
                !showAdapt ? (
                  <button
                    onClick={() => setShowAdapt(true)}
                    className="flex items-center gap-1 text-purple-300 hover:text-purple-200 text-[11px] font-medium"
                  >
                    <Wand2 className="w-3 h-3" /> Produit différent de l'exemple ? Laissez TeckiA adapter
                  </button>
                ) : (
                  <div className="bg-black/25 rounded-xl p-3 space-y-2 border border-purple-500/20">
                    <label className="text-gray-400 text-xs">Expliquez votre produit / ce qui change</label>
                    <textarea
                      value={explanation}
                      onChange={e => setExplanation(e.target.value)}
                      rows={2}
                      placeholder="Ex. « Mon produit n'est pas comme ça, c'est en fait un thermos, faites qu'elle le présente pareil... »"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
                    />
                    <button
                      onClick={adaptTemplate}
                      disabled={adapting}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium disabled:opacity-50"
                    >
                      {adapting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Adapter avec TeckiA
                    </button>
                  </div>
                )
              )}

              {!insufficientCredit && error && <p className="text-red-400 text-xs">{error}</p>}

              <div className="flex gap-2 pt-1">
                {!selectedTemplate && (
                  <button
                    onClick={() => generate(true)}
                    disabled={generating}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Améliorer avec TeckiA
                  </button>
                )}
                <button
                  onClick={() => generate(false)}
                  disabled={generating}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold disabled:opacity-50 ${
                    selectedTemplate
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white'
                      : 'bg-black/30 border border-white/15 hover:border-white/30 text-gray-200 font-medium'
                  }`}
                >
                  <ArrowRight className="w-3.5 h-3.5" /> {selectedTemplate ? 'Générer' : 'Utiliser tel quel'}
                </button>
              </div>
            </div>
          ) : step === 'generating' ? (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <TeckiaOrb size="lg" />
              <div className="text-white font-medium text-sm">{pub ? 'TeckiA compose votre pub...' : 'TeckiA compose votre publication...'}</div>
              <div className="text-gray-400 text-xs text-center max-w-xs">
                {mediaKind === 'video' ? "La génération vidéo peut prendre 1 à 3 minutes." : "Quelques secondes suffisent en général."}
              </div>
            </div>
          ) : step === 'result' && resultPost ? (
            <div className="space-y-3">
              {resultPost.media_kind === 'video' ? (
                <video src={resultPost.image_url} controls className="w-full rounded-xl bg-black max-h-72 object-contain" />
              ) : (
                <img src={resultPost.image_url} alt="" className="w-full rounded-xl object-cover max-h-72" />
              )}

              <button
                onClick={regenerate}
                disabled={acting}
                className="flex items-center gap-1.5 text-purple-300 hover:text-purple-200 text-xs font-medium"
              >
                <RefreshCcw className="w-3.5 h-3.5" /> Régénérer avec un autre prompt
              </button>

              <div>
                <label className="text-gray-400 text-xs">{pub ? 'Texte d’accompagnement (facultatif)' : 'Légende de la publication'}</label>
                <textarea
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  rows={3}
                  placeholder={pub ? 'Un texte à copier avec votre pub…' : 'Écrivez votre légende Facebook...'}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500"
                />
              </div>

              {error && <p className="text-red-400 text-xs">{error}</p>}

              {pub ? (
                <div className="flex gap-2 pt-1">
                  <a
                    href={resultPost.image_url}
                    download={`teckia_pub_${resultPost.id}.${resultPost.media_kind === 'video' ? 'mp4' : 'jpg'}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-semibold"
                  >
                    <Download className="w-3.5 h-3.5" /> Télécharger
                  </a>
                  <button
                    onClick={publishNow}
                    disabled={acting}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Enregistrer dans mes pubs
                  </button>
                </div>
              ) : scheduling ? (
                <div className="bg-black/25 rounded-xl p-3 space-y-2 border border-purple-500/20">
                  <label className="text-gray-400 text-xs flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Date et heure de publication</label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={e => setScheduledAt(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={confirmSchedule}
                      disabled={acting || !scheduledAt}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50"
                    >
                      {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />} Confirmer la planification
                    </button>
                    <button onClick={() => setScheduling(false)} className="px-3 py-2 rounded-lg bg-black/30 text-gray-300 text-xs">Annuler</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={publishNow}
                    disabled={acting}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publier maintenant
                  </button>
                  <button
                    onClick={() => setScheduling(true)}
                    disabled={acting}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-black/30 border border-white/15 hover:border-white/30 text-gray-200 text-xs font-medium disabled:opacity-50"
                  >
                    <Calendar className="w-3.5 h-3.5" /> Planifier
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  ), document.body);
}
